import { useState, useEffect, useCallback, useRef, useContext, createContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import notificationService from '../services/notificationService';

// The badge is refreshed on things the user actually does, not on a timer.
// Don't repeat within this window however many of them happen at once.
const REFRESH_MIN_GAP_MS = 30 * 1000;

/**
 * The parts of the notification state that every screen shows the same way, and
 * that cost something to keep fresh: the unread badge and the smart alerts.
 *
 * They live in a provider because the hook was being instantiated three times at
 * once — Header, Dashboard and the Notifications page — and each instance ran
 * its own 60-second poll and its own three mount requests. Sitting on the
 * Dashboard meant two identical unread-count requests every minute, forever, in
 * every open tab. That kind of steady background volume is what got this
 * database user's grants revoked by the host's abuse prevention on 2026-09-04.
 *
 * There is no timer at all now. Fetching only on mount would not work — Layout
 * is persistent, so it does not remount when you navigate — and a badge frozen
 * at whatever it was when the tab opened is worse than useless: an attendance
 * approval would sit unseen all morning. So it refreshes on the two things that
 * mean someone is actually working: coming back to the tab, and moving between
 * pages. Both are throttled. Someone sitting still costs nothing; someone using
 * the app keeps a current badge for about one query a page.
 *
 * Deliberately NOT shared: the notification list and its filter. Filtering is
 * done server-side (`fetchNotifications(type)` refetches), so sharing one list
 * would mean the Notifications page and the Header dropdown fighting over which
 * filter is active — picking "Attendance" on the page would silently retab the
 * dropdown. One extra list request on the one screen that shows both is a much
 * better trade than a filter that moves on its own.
 */
const NotificationsContext = createContext(null);

// `enabled` false (the board-only tab) makes this a no-op provider: no badge poll,
// no smart-alerts request, for a page that shows neither.
export function NotificationsProvider({ children, enabled = true }) {
	const [unreadCount, setUnreadCount] = useState(0);
	const [smartAlerts, setSmartAlerts] = useState([]);
	const lastCountAtRef = useRef(0);
	const { pathname } = useLocation();

	const fetchUnreadCount = useCallback(async ({ force = false } = {}) => {
		const now = Date.now();
		if (!force && now - lastCountAtRef.current < REFRESH_MIN_GAP_MS) return;
		// Stamped before the request: visibilitychange and focus both fire on a
		// single alt-tab back, and a navigation can land in the same instant.
		lastCountAtRef.current = now;
		try {
			const response = await notificationService.getUnreadCount();
			if (response?.rescode === 1) {
				setUnreadCount(response.data?.count || 0);
			}
		} catch (err) {
			console.error('Failed to fetch unread count:', err);
		}
	}, []);

	const fetchSmartAlerts = useCallback(async () => {
		try {
			const response = await notificationService.getSmartAlerts();
			if (response?.rescode === 1) {
				setSmartAlerts(Array.isArray(response.data) ? response.data : []);
			}
		} catch (err) {
			console.error('Failed to fetch smart alerts:', err);
		}
	}, []);

	useEffect(() => {
		if (!enabled) return;
		fetchUnreadCount({ force: true });
		fetchSmartAlerts();
	}, [enabled, fetchUnreadCount, fetchSmartAlerts]);

	// Coming back to the tab.
	useEffect(() => {
		if (!enabled) return undefined;
		const onVisible = () => {
			if (document.visibilityState === 'hidden') return;
			fetchUnreadCount();
		};
		document.addEventListener('visibilitychange', onVisible);
		window.addEventListener('focus', onVisible);
		return () => {
			document.removeEventListener('visibilitychange', onVisible);
			window.removeEventListener('focus', onVisible);
		};
	}, [enabled, fetchUnreadCount]);

	// Moving between pages. Layout does not remount on navigation, so without
	// this the badge would never change for someone who does not switch tabs.
	useEffect(() => {
		if (!enabled) return;
		fetchUnreadCount();
	}, [enabled, pathname, fetchUnreadCount]);

	const value = useMemo(
		() => ({ unreadCount, setUnreadCount, smartAlerts, fetchUnreadCount, fetchSmartAlerts }),
		[unreadCount, smartAlerts, fetchUnreadCount, fetchSmartAlerts]
	);

	return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications() {
	const shared = useContext(NotificationsContext);
	if (!shared) {
		throw new Error('useNotifications must be used inside <NotificationsProvider> (mounted in the portal Layout).');
	}
	const { unreadCount, setUnreadCount, smartAlerts, fetchUnreadCount } = shared;

	const [notifications, setNotifications] = useState([]);
	const [loading, setLoading] = useState(false);
	const [filter, setFilter] = useState('all');
	const filterRef = useRef('all');

	const fetchNotifications = useCallback(async (type = 'all') => {
		try {
			filterRef.current = type;
			setLoading(true);
			const response = await notificationService.getList({ type, page: 1, limit: 50 });
			if (response?.rescode === 1) {
				setNotifications(response.data?.notifications || []);
			}
		} catch (err) {
			console.error('Failed to fetch notifications:', err);
		} finally {
			setLoading(false);
		}
	}, []);

	const markAsRead = useCallback(async (id) => {
		try {
			await notificationService.markAsRead({ id });
			setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
			setUnreadCount(prev => Math.max(0, prev - 1));
		} catch (err) {
			console.error('Failed to mark notification as read:', err);
		}
	}, [setUnreadCount]);

	const markAllAsRead = useCallback(async () => {
		try {
			await notificationService.markAllAsRead();
			setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
			setUnreadCount(0);
		} catch (err) {
			console.error('Failed to mark all as read:', err);
		}
	}, [setUnreadCount]);

	const deleteNotification = useCallback(async (id) => {
		try {
			const response = await notificationService.delete({ id });
			// The backend refuses to delete an attendance change that is still
			// awaiting a decision — it has to stay in the list until it is approved
			// or cancelled. rescode, not the HTTP status, is the success signal.
			if (response?.rescode !== 1) {
				toast.error(response?.message || 'This notification cannot be dismissed yet.');
				return false;
			}
			setNotifications(prev => prev.filter(n => n.id !== id));
			setUnreadCount(prev => {
				const notif = notifications.find(n => n.id === id);
				return notif && !notif.is_read ? Math.max(0, prev - 1) : prev;
			});
			return true;
		} catch (err) {
			toast.error(err?.message || 'Failed to delete notification.');
			return false;
		}
	}, [notifications, setUnreadCount]);

	// Re-pull the list (current filter) + badge — used after an action that
	// rewrites notifications server-side, e.g. an attendance approve/cancel.
	const refresh = useCallback(async () => {
		await Promise.all([fetchNotifications(filterRef.current), fetchUnreadCount({ force: true })]);
	}, [fetchNotifications, fetchUnreadCount]);

	const changeFilter = useCallback((type) => {
		setFilter(type);
		fetchNotifications(type);
	}, [fetchNotifications]);

	// Initial load of this screen's own list. The badge and smart alerts are
	// already loaded once by the provider, however many screens are mounted.
	useEffect(() => {
		fetchNotifications();
	}, [fetchNotifications]);

	return {
		notifications,
		unreadCount,
		loading,
		filter,
		smartAlerts,
		fetchNotifications,
		fetchUnreadCount,
		markAsRead,
		markAllAsRead,
		deleteNotification,
		changeFilter,
		refresh,
	};
}

/**
 * For a screen that only needs the badge — the Dashboard reads nothing else.
 * Using the full hook there cost a list request and a smart-alerts request per
 * mount for a single number.
 */
export function useUnreadCount() {
	const shared = useContext(NotificationsContext);
	if (!shared) {
		throw new Error('useUnreadCount must be used inside <NotificationsProvider> (mounted in the portal Layout).');
	}
	return shared.unreadCount;
}
